import { Fragment, useRef } from 'react';
import { useGSAP } from '@gsap/react';
import gsap from 'gsap';
import {
  LuBrain,
  LuFileText,
  LuUser,
  LuUsers,
} from 'react-icons/lu';

import { createStepHighlightCycle, killStepHighlightTweens } from '../../lib/gsapHighlight';
import SbrIcon from './SbrIcon';

const heroGraphNodes = [
  {
    Icon: LuFileText,
    step: '01',
    title: 'Understand',
    desc: 'Align on goals, requirements, team dynamics, and your ideal candidate profile.',
  },
  {
    Icon: LuBrain,
    step: '02',
    title: 'AI-Powered Search',
    desc: 'Scan millions of data points to discover and shortlist top talent fast.',
  },
  {
    Icon: LuUsers,
    step: '03',
    title: 'Human Validation',
    desc: 'Experts validate fit, experience, and cultural alignment before you interview.',
  },
  {
    Icon: LuUser,
    step: '04',
    title: 'Exclusive Shortlist',
    desc: 'Receive a curated, vetted shortlist ready for decisive hiring conversations.',
  },
] as const;

const DIM_SHADOW = '0 0 0 1px rgba(43, 147, 240, 0.2), 0 0 12px rgba(43, 147, 240, 0.15)';
const ACTIVE_SHADOW =
  '0 0 0 1px rgba(0, 194, 171, 0.45), 0 0 24px rgba(0, 194, 171, 0.75), 0 0 40px rgba(43, 147, 240, 0.35)';

export default function HiwHeroWorkflow() {
  const rootRef = useRef<HTMLDivElement | null>(null);
  const infoRef = useRef<HTMLDivElement | null>(null);

  useGSAP(
    () => {
      if (!rootRef.current) return;

      const rings = rootRef.current.querySelectorAll<HTMLElement>('.hiw-graph__node-ring');
      const connectors = rootRef.current.querySelectorAll<HTMLElement>('.hiw-graph__connector');
      const icons = rootRef.current.querySelectorAll<HTMLElement>('.hiw-graph__icon');
      if (!rings.length) return;

      const reducedMotion = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
      let cycleTween: gsap.core.Timeline | null = null;

      const setActiveStep = (index: number) => {
        const node = heroGraphNodes[index];
        const info = infoRef.current;
        if (!info || !node) return;

        const stepEl = info.querySelector<HTMLElement>('[data-info-step]');
        const titleEl = info.querySelector<HTMLElement>('[data-info-title]');
        const descEl = info.querySelector<HTMLElement>('[data-info-desc]');
        if (stepEl) stepEl.textContent = `Step ${node.step}`;
        if (titleEl) titleEl.textContent = node.title;
        if (descEl) descEl.textContent = node.desc;
      };

      const highlightStep = (index: number) => {
        killStepHighlightTweens(rings, connectors, icons);
        if (infoRef.current) {
          gsap.killTweensOf(infoRef.current);
        }

        rings.forEach((ring, j) => {
          const isActive = j === index;
          if (isActive) {
            gsap.to(ring, {
              boxShadow: ACTIVE_SHADOW,
              scale: 1.1,
              borderColor: 'rgba(0, 194, 171, 0.7)',
              duration: 0.35,
              ease: 'back.out(1.6)',
              overwrite: true,
            });
          } else {
            gsap.set(ring, {
              boxShadow: DIM_SHADOW,
              scale: 0.94,
              borderColor: 'rgba(43, 147, 240, 0.45)',
            });
          }
        });

        icons.forEach((icon, j) => {
          if (j === index) {
            gsap.to(icon, { color: '#00c2ab', duration: 0.35, overwrite: true });
          } else {
            gsap.set(icon, { color: '#ffffff' });
          }
        });

        connectors.forEach((conn, j) => {
          const isLeading = j === index - 1;
          if (isLeading) {
            gsap.to(conn, {
              opacity: 1,
              scaleX: 1,
              background: 'linear-gradient(90deg, rgba(43, 147, 240, 0.4), rgba(0, 194, 171, 0.7))',
              duration: 0.35,
              overwrite: true,
            });
          } else {
            gsap.set(conn, {
              opacity: 0.3,
              scaleX: 0.95,
              background: 'rgba(43, 147, 240, 0.35)',
            });
          }
        });

        setActiveStep(index);
        if (infoRef.current) {
          gsap.fromTo(
            infoRef.current,
            { opacity: 0.6, y: 4 },
            { opacity: 1, y: 0, duration: 0.35, ease: 'power2.out', overwrite: true },
          );
        }
      };

      rings.forEach((ring, i) => {
        gsap.set(ring, {
          boxShadow: i === 0 ? ACTIVE_SHADOW : DIM_SHADOW,
          scale: i === 0 ? 1.05 : 0.94,
          opacity: 1,
        });
      });
      setActiveStep(0);

      const cleanup = () => {
        cycleTween?.kill();
        cycleTween = null;
      };

      if (reducedMotion) return cleanup;

      cycleTween = createStepHighlightCycle(heroGraphNodes.length, highlightStep);

      return cleanup;
    },
    { scope: rootRef },
  );

  const first = heroGraphNodes[0];

  return (
    <div
      ref={rootRef}
      className="hiw-graph hiw-graph--horizontal"
      aria-label="Recruiting workflow from requirements to successful hire"
    >
      <div className="hiw-graph__row">
        {heroGraphNodes.map((node, i) => (
          <Fragment key={node.step}>
            {i > 0 ? <div className="hiw-graph__connector" aria-hidden /> : null}
            <div className="hiw-graph__node">
              <div className="hiw-graph__node-ring">
                <SbrIcon icon={node.Icon} size={28} className="hiw-graph__icon" />
              </div>
            </div>
          </Fragment>
        ))}
      </div>

      <div ref={infoRef} className="hiw-graph__info" aria-live="polite">
        <span className="hiw-graph__info-step" data-info-step>
          Step {first.step}
        </span>
        <div className="hiw-graph__info-title" data-info-title>
          {first.title}
        </div>
        <p className="hiw-graph__info-desc mb-0" data-info-desc>
          {first.desc}
        </p>
      </div>
    </div>
  );
}
